import { useMutation, useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { createColumnHelper } from '@tanstack/react-table'
import type { ResultOf } from 'gql.tada'
import { useCallback, useMemo } from 'react'
import { DataTable, FAIcon } from '@/components'
import {
  CREATE_TAX_UNIT,
  DELETE_TAX_UNIT,
  PATCH_TAX_UNIT,
} from '@/modules/companies/mutations'
import { ALL_TAX_UNITS_QUERY } from '@/modules/companies/queries'
import { useConfirm, useOnErrorAuto } from '@/utils/hooks'
import { Header, OldAddButton, OldSection, SectionHeader } from '../common'
import type { TaxUnitForm } from '../types'
import { TaxUnitPrompt } from './tax-unit-prompt'

type TaxUnit = ResultOf<typeof ALL_TAX_UNITS_QUERY>['allTaxUnits'][number]

const column = createColumnHelper<TaxUnit>()

export const TaxUnitSettings: React.JSX.CFC = () => {
  const translations = useTranslate({
    taxUnits: 'settings.employer-tax.tax-units',
    info: 'settings.employer-tax.tax-units-info',
    addTaxUnit: 'settings.employer-tax.add-tax-unit',
    zone: 'settings.employer-tax.zone',
    zoneName: ['settings.employer-tax.zone-name', { zone: '' }],
    deleteTitle: 'settings.employer-tax.delete-tax-unit-title',
    deleteConfirm: ['settings.employer-tax.delete-tax-unit', { name: '' }],

    name: 'common.name',
    users: 'common.users',
    noResults: 'common.no-results',
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const onErrorAuto = useOnErrorAuto()

  const { data, loading } = useQuery(ALL_TAX_UNITS_QUERY, {
    onError: onErrorAuto(),
  })
  const taxUnits = useMemo(() => data?.allTaxUnits ?? [], [data])

  const mutationOptions = {
    refetchQueries: [ALL_TAX_UNITS_QUERY],
    onError: onErrorAuto(),
  }
  const [createTaxUnit, { loading: creating }] = useMutation(
    CREATE_TAX_UNIT,
    mutationOptions
  )
  const [patchTaxUnit, { loading: patching }] = useMutation(
    PATCH_TAX_UNIT,
    mutationOptions
  )
  const [deleteTaxUnit, { loading: deleting }] = useMutation(
    DELETE_TAX_UNIT,
    mutationOptions
  )

  async function handleCreate() {
    const { data } = await addPrompt<TaxUnitForm | null>(resolve => (
      <TaxUnitPrompt onSubmit={resolve} />
    ))
    if (!data) return

    await createTaxUnit({ variables: { input: data } })
  }

  const handleEdit = useCallback(
    async (taxUnit: TaxUnit) => {
      const { data } = await addPrompt<TaxUnitForm | null>(resolve => (
        <TaxUnitPrompt
          initialData={{
            name: taxUnit.name,
            zoneCode: taxUnit.zoneCode,
            users: taxUnit.users.map(user => user.id),
          }}
          onSubmit={resolve}
        />
      ))
      if (!data) return

      await patchTaxUnit({ variables: { id: taxUnit.id, input: data } })
    },
    [addPrompt, patchTaxUnit]
  )

  const handleDelete = useCallback(
    async (taxUnit: TaxUnit) => {
      const { data: answer } = await confirm(
        translations.deleteConfirm({ name: taxUnit.name }),
        translations.deleteTitle
      )
      if (!answer) return

      await deleteTaxUnit({ variables: { id: taxUnit.id } })
    },
    [confirm, deleteTaxUnit, translations]
  )

  const columns = useMemo(
    () => [
      column.accessor('name', {
        header: translations.name,
        enableSorting: true,
      }),
      column.accessor('zoneCode', {
        header: translations.zone,
        enableSorting: true,
        cell: info => translations.zoneName({ zone: info.getValue() }),
      }),
      column.accessor(taxUnit => taxUnit.users.length, {
        id: 'users',
        header: translations.users,
        enableSorting: true,
      }),
      column.display({
        id: 'edit',
        meta: { narrow: true },
        cell: info => (
          <FAIcon
            className="text-gray-400 hover:text-secondary"
            icon="edit"
            cursor="pointer"
            onClick={() => handleEdit(info.row.original)}
          />
        ),
      }),
      column.display({
        id: 'delete',
        meta: { narrow: true },
        cell: info => (
          <FAIcon
            className="text-gray-400 hover:text-red-500"
            icon="times"
            cursor="pointer"
            onClick={() => handleDelete(info.row.original)}
          />
        ),
      }),
    ],
    [handleDelete, handleEdit, translations]
  )

  return (
    <OldSection>
      <Header>
        <SectionHeader loading={loading || creating || patching || deleting}>
          {translations.taxUnits}
        </SectionHeader>

        <OldAddButton
          addText={translations.addTaxUnit}
          adding={false}
          onClick={handleCreate}
        />
      </Header>

      <p className="mb-4 text-sm text-stone-500">{translations.info}</p>

      <DataTable
        columns={columns}
        data={taxUnits}
        noDataText={translations.noResults}
      />
    </OldSection>
  )
}
